import { createStyles, Grid } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import { MaterialPalette, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import postal from 'postal';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import RxdbManager from '../../rxdb/RxdbManager';
import { useRxDB } from '../../rxdb/useRxDB';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { IMAGE_PATH } from '../../utilities/constants';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      height: '100vh',
    },
    card: {
      maxWidth: '450px',
      padding: '70px 40px',
      borderRadius: '10px',
    },
    imageContainer: {
      marginBottom: '50px',
      textAlign: 'center',
      position: 'relative',
    },
    icon: {
      '& svg': {
        marginTop: '5px',
      },
    },
    offlineText: {
      marginLeft: '10px',
    },
    infoTextContainer: {
      display: 'flex',
      marginTop: '20px',
    },
    progressBar: {
      width: '188px',
      position: 'absolute',
      top: '84px',
      left: 'calc(50% - 95px)',
      '& > div': {
        backgroundColor: MaterialPalette.common.white,
        height: '2px',
      },
    },
  }),
);

interface Props {
  loginHandler: () => void;
}

const Loading: React.FC<Props> = ({ loginHandler }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [syncCount, setSyncCount] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const isOnline = useOnlineStatus();
  const { SiteDiscipline } = useMst();
  const { db } = useRxDB();

  const increaseCount = () => {
    setSyncCount(prev => prev + 1);
  };

  useEffect(() => {
    if (syncCount > 0) {
      const percent = Math.round((syncCount / db?.syncStates?.length) * 100);
      setProgress(percent);
    }
    if (isOnline && !!db?.syncStates?.length && syncCount >= db?.syncStates.length) {
      history.push('/work-packages');
    }
  }, [syncCount, db?.isInitialized, isOnline, db?.syncStates]);

  useEffect(() => {
    const subArray: Subscription[] = [];

    const postalSub = postal.subscribe({
      channel: 'sync',
      topic: 'sync.online',
      callback: () => {
        RxdbManager.instance.syncStates.forEach(state => {
          const sub = state.initialReplicationComplete$.subscribe(
            (initialReplicationComplete: boolean) => {
              if (initialReplicationComplete === true) increaseCount();
            },
          );
          subArray.push(sub);
        });
      },
    });

    return () => {
      postalSub.unsubscribe();
      subArray.forEach(sub => sub.unsubscribe());
    };
  }, []);

  useEffect(() => {
    const firstLogin = !(SiteDiscipline.siteId && SiteDiscipline.disciplineId);
    if (firstLogin) {
      loginHandler();
    }
    if (!isOnline && !firstLogin) {
      // If we are not online take them to work packages screen
      history.push('/work-packages');
    }
  }, [SiteDiscipline.siteId, SiteDiscipline.disciplineId, isOnline]);

  return (
    <>
      <Grid container className={classes.root} justifyContent="center" alignItems="center">
        <Grid item xs={8} md={6} className={classes.card}>
          <div className={classes.imageContainer}>
            <img src={`${IMAGE_PATH}/nutrien_dark_noline.svg`} />
            <Typography variant="h4">AIMS - Inspection</Typography>
            <div className={classes.progressBar}>
              <LinearProgress variant="determinate" value={progress} />
            </div>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default observer(Loading);
